import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  Loader2, RefreshCw, Activity, Users, Building2, ClipboardCheck,
  GitBranch, RotateCcw, Repeat, Clock, ShieldAlert, Eye, Paperclip, Info,
} from "lucide-react";
import { API } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUS_BADGE = {
  delivered: "bg-emerald-100 text-emerald-700 border-emerald-200",
  completed: "bg-emerald-100 text-emerald-700 border-emerald-200",
  in_sequence: "bg-blue-100 text-blue-700 border-blue-200",
  queued: "bg-blue-100 text-blue-700 border-blue-200",
  not_started: "bg-slate-100 text-slate-600 border-slate-200",
  awaiting_authorisation: "bg-amber-100 text-amber-800 border-amber-300",
  held: "bg-amber-100 text-amber-700 border-amber-200",
  stopped: "bg-rose-100 text-rose-700 border-rose-200",
  removed: "bg-slate-200 text-slate-600 border-slate-300",
  unknown: "bg-slate-100 text-slate-500 border-slate-200",
};

const fmt = (s) =>
  s ? new Date(s).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—";

export default function CampaignProgressPanel({ campaignId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [review, setReview] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);

  const load = useCallback(async (initial = false) => {
    if (initial) setLoading(true);
    try {
      const r = await fetch(`${API}/campaigns/bespoke/${campaignId}/progress`, { credentials: "include" });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Failed");
      setData(d);
    } catch (e) {
      toast.error(`Could not load progress (${e.message || "error"})`);
    } finally { setLoading(false); }
  }, [campaignId]);

  useEffect(() => { load(true); }, [load]);

  const openReview = async () => {
    setReviewOpen(true); setReviewLoading(true); setReview(null);
    try {
      const r = await fetch(`${API}/campaigns/bespoke/${campaignId}/pre-send-review`, { credentials: "include" });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Failed");
      setReview(d);
    } catch (e) {
      toast.error(`Could not load review (${e.message || "error"})`);
    } finally { setReviewLoading(false); }
  };

  const applyToUnsent = async () => {
    setBusy("apply");
    try {
      const r = await fetch(`${API}/campaigns/bespoke/${campaignId}/sequence/apply-to-unsent`, {
        method: "POST", credentials: "include",
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Failed");
      toast.success(`Upgraded ${d.recipients_upgraded} recipient(s) to v${d.version}. ${d.note}`);
      load();
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  const reopen = async (rid) => {
    setBusy(rid);
    try {
      const r = await fetch(`${API}/campaigns/bespoke/${campaignId}/recipients/${rid}/reopen`, {
        method: "POST", credentials: "include",
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Failed");
      toast.success(`Reopened — resumes from step ${d.resumes_from_step}. History preserved.`);
      load();
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  const reEnroll = async (rid) => {
    if (!window.confirm("Re-enroll this recipient from step 1? Prior history is archived and explicit schedule authorisation is required again before anything sends.")) return;
    setBusy(rid);
    try {
      const r = await fetch(`${API}/campaigns/bespoke/${campaignId}/recipients/${rid}/re-enroll`, {
        method: "POST", credentials: "include",
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Failed");
      toast.success("Re-enrolled at step 1. Prior history archived; needs schedule authorisation to send.");
      load();
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  if (loading) {
    return <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-indigo-600" /></div>;
  }
  if (!data) return null;

  const cov = data.coverage || {};
  const summary = data.status_summary || {};

  return (
    <div className="space-y-3" data-testid={`progress-panel-${campaignId}`}>
      {/* Header: fixed progress + refresh + pre-send review */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          <Activity className="w-4 h-4 text-indigo-600" /> Progress
          <span className="text-indigo-600" data-testid={`progress-pct-${campaignId}`}>{data.progress_pct}%</span>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={openReview} data-testid={`pre-send-review-btn-${campaignId}`}>
            <Eye className="w-3.5 h-3.5 mr-1" /> Pre-send review
          </Button>
          <Button size="sm" variant="outline" onClick={() => load()} data-testid={`progress-refresh-${campaignId}`}>
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Fixed progress bar */}
      <div className="h-2.5 w-full rounded-full bg-slate-200 overflow-hidden" data-testid={`progress-bar-${campaignId}`}>
        <div className="h-full bg-indigo-600 transition-all" style={{ width: `${data.progress_pct}%` }} />
      </div>

      {/* Reply-monitoring hold banner — follow-ups are HELD at dispatch while unhealthy */}
      {data.reply_monitoring?.holds_follow_ups && (
        <div
          className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
          data-testid={`reply-monitoring-hold-${campaignId}`}
        >
          <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
          <div>
            <div className="font-semibold" data-testid={`reply-monitoring-hold-title-${campaignId}`}>
              Follow-ups paused — reply monitoring unhealthy
            </div>
            <div className="text-amber-800" data-testid={`reply-monitoring-hold-reason-${campaignId}`}>
              {data.reply_monitoring.reason || "Reply monitoring is not healthy."}
              {data.reply_monitoring.affected_follow_ups > 0 && (
                <> {" "}Already-queued follow-ups ({data.reply_monitoring.affected_follow_ups}) are held at dispatch until monitoring recovers. First emails are unaffected.</>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Status summary chips */}
      <div className="flex flex-wrap gap-1.5" data-testid={`progress-summary-${campaignId}`}>
        {Object.entries(summary).map(([k, v]) => (
          <Badge key={k} variant="outline" className={`${STATUS_BADGE[k] || STATUS_BADGE.unknown} text-xs`}>
            {v} {k.replace(/_/g, " ")}
          </Badge>
        ))}
      </div>

      {/* Reconciled coverage counts */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid={`coverage-${campaignId}`}>
        <div className="bg-white border rounded-lg p-2.5">
          <div className="text-xs text-slate-400 flex items-center gap-1"><Building2 className="w-3 h-3" /> Organisations</div>
          <div className="text-sm font-semibold text-slate-700">{cov.organisations_with_approved_contact}/{cov.organisations_targeted}</div>
          <div className="text-[10px] text-slate-400">with an approved contact</div>
        </div>
        <div className="bg-white border rounded-lg p-2.5">
          <div className="text-xs text-slate-400 flex items-center gap-1"><ClipboardCheck className="w-3 h-3" /> Contacts</div>
          <div className="text-sm font-semibold text-slate-700">{cov.contacts_reviewed}</div>
          <div className="text-[10px] text-slate-400">reviewed (approved/rejected)</div>
        </div>
        <div className="bg-white border rounded-lg p-2.5">
          <div className="text-xs text-slate-400 flex items-center gap-1"><Users className="w-3 h-3" /> Recipients</div>
          <div className="text-sm font-semibold text-slate-700">{cov.recipients_total}</div>
          <div className="text-[10px] text-slate-400">on this campaign</div>
        </div>
        <div className="bg-white border rounded-lg p-2.5">
          <div className="text-xs text-slate-400 flex items-center gap-1"><Activity className="w-3 h-3" /> Delivered</div>
          <div className="text-sm font-semibold text-slate-700">{cov.recipients_delivered}</div>
          <div className="text-[10px] text-slate-400">reached / complete</div>
        </div>
      </div>
      <p className="text-[11px] text-slate-400 flex items-start gap-1">
        <Info className="w-3 h-3 mt-0.5 shrink-0" /> {cov.note} {data.scheduler_note}
      </p>

      {/* Per-recipient progress table */}
      <div className="border rounded-lg bg-white overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Recipient</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Next action</TableHead>
              <TableHead>Expected</TableHead>
              <TableHead>Last activity</TableHead>
              <TableHead>Owner</TableHead>
              <TableHead>Hold / stop</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.rows.map((r) => (
              <TableRow key={r.recipient_id} data-testid={`progress-row-${r.recipient_id}`}>
                <TableCell className="text-xs">
                  <div className="font-medium text-slate-700">{r.name || "—"}</div>
                  <div className="text-slate-400 truncate max-w-[180px]">{r.email}</div>
                  {r.company_name && <div className="text-slate-400 truncate max-w-[180px]">{r.company_name}</div>}
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className={`${STATUS_BADGE[r.status] || STATUS_BADGE.unknown} text-xs`}
                    data-testid={`progress-status-${r.recipient_id}`}>
                    {(r.status || "unknown").replace(/_/g, " ")}
                  </Badge>
                  {r.seq_version != null && <div className="text-[10px] text-slate-400 mt-0.5">seq v{r.seq_version}</div>}
                </TableCell>
                <TableCell className="text-xs text-slate-600 max-w-[200px]">{r.next_action}</TableCell>
                <TableCell className="text-xs text-slate-500 whitespace-nowrap">
                  {r.expected_time ? <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{fmt(r.expected_time)}</span> : "—"}
                </TableCell>
                <TableCell className="text-xs text-slate-500 whitespace-nowrap">{fmt(r.last_activity)}</TableCell>
                <TableCell className="text-xs text-slate-500 truncate max-w-[140px]">{r.owner || "—"}</TableCell>
                <TableCell className="text-xs">
                  {r.hold_stop_reason
                    ? <span className="flex items-center gap-1 text-rose-600"><ShieldAlert className="w-3 h-3" />{r.hold_stop_reason.replace(/_/g, " ")}</span>
                    : "—"}
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  {["stopped", "completed"].includes(r.status) && (
                    <div className="flex items-center gap-1 justify-end">
                      <button onClick={() => reopen(r.recipient_id)} disabled={busy === r.recipient_id}
                        className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 disabled:opacity-50"
                        data-testid={`reopen-${r.recipient_id}`}>
                        {busy === r.recipient_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />} Reopen
                      </button>
                      <button onClick={() => reEnroll(r.recipient_id)} disabled={busy === r.recipient_id}
                        className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 disabled:opacity-50"
                        data-testid={`re-enroll-${r.recipient_id}`}>
                        <Repeat className="w-3 h-3" /> Re-enroll
                      </button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {data.rows.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-6 text-sm">No recipients yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Published sequence version + apply-to-unsent */}
      <div className="bg-violet-50/60 border border-violet-200 rounded-lg p-3 flex items-center justify-between gap-2 flex-wrap"
        data-testid={`seq-version-bar-${campaignId}`}>
        <div className="text-xs text-violet-800 flex items-center gap-1.5">
          <GitBranch className="w-3.5 h-3.5" />
          Existing enrollments keep the sequence version they were pinned to. Apply the current
          published version to UNSENT follow-ups only (already-sent steps keep their original content).
        </div>
        <Button size="sm" variant="outline" onClick={applyToUnsent} disabled={busy === "apply"}
          className="h-7 text-xs border-violet-300 text-violet-800 hover:bg-violet-100"
          data-testid={`apply-to-unsent-${campaignId}`}>
          {busy === "apply" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <GitBranch className="w-3.5 h-3.5 mr-1" />}
          Apply current version to unsent
        </Button>
      </div>

      {/* Pre-send review dialog */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid={`pre-send-dialog-${campaignId}`}>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Eye className="w-4 h-4" /> Pre-send review</DialogTitle></DialogHeader>
          {reviewLoading || !review ? (
            <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-indigo-600" /></div>
          ) : (
            <div className="space-y-4 text-sm">
              {/* Timing / pacing / capacity */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="review-pacing">
                <Stat label="Sendable now" value={review.sendable_count} />
                <Stat label="Timezone" value={review.timezone} />
                <Stat label="Send window" value={`${review.window?.start}–${review.window?.end}`} />
                <Stat label="Pacing" value={`every ${review.pacing_minutes} min`} />
                <Stat label="Batch" value={`${review.batch_size} / gap ${review.batch_gap_minutes}m`} />
                <Stat label="Daily cap (shared)" value={`${review.capacity?.remaining_today} left of ${review.capacity?.daily_limit}`} />
              </div>
              <div className="bg-slate-50 border rounded-lg p-3 text-xs space-y-1" data-testid="review-estimate">
                <div>Sender: <strong>{review.capacity?.sender_email || "—"}</strong> · sent today: {review.capacity?.sent_today}</div>
                <div>First send: <strong>{fmt(review.first_send_at)}</strong></div>
                <div>Estimated completion: <strong>{fmt(review.estimated_completion_display || review.estimated_completion)}</strong></div>
                <div className="text-slate-400">{review.scheduler_note}</div>
              </div>

              {/* Greetings */}
              <section>
                <h4 className="text-xs font-semibold text-slate-600 mb-1">Resolved greetings ({review.greetings?.length || 0})</h4>
                <div className="border rounded-lg divide-y max-h-40 overflow-y-auto" data-testid="review-greetings">
                  {(review.greetings || []).map((g) => (
                    <div key={g.recipient_id} className="flex items-center gap-2 px-3 py-1.5 text-xs">
                      <span className="text-slate-700 flex-1 truncate">{g.salutation || <em className="text-rose-500">unresolved</em>} — {g.email}</span>
                      <Badge variant="outline" className={g.ready ? "bg-emerald-50 text-emerald-600 border-emerald-200 text-[10px]" : "bg-amber-50 text-amber-700 border-amber-200 text-[10px]"}>
                        {g.ready ? "ready" : "hold"}
                      </Badge>
                    </div>
                  ))}
                  {(review.greetings || []).length === 0 && <div className="px-3 py-2 text-xs text-slate-400">No greetings pending.</div>}
                </div>
              </section>

              {/* Sequence content + attachments */}
              {review.sequence?.enabled && (
                <section data-testid="review-sequence">
                  <h4 className="text-xs font-semibold text-slate-600 mb-1">Sequence (v{review.sequence.version}) — {review.sequence.steps?.length} step(s)</h4>
                  <div className="space-y-2">
                    {(review.sequence.steps || []).map((s) => (
                      <div key={s.step} className="border rounded-lg p-2.5" data-testid={`review-step-${s.step}`}>
                        <div className="flex items-center gap-2 text-xs font-medium text-slate-700">
                          Step {s.step}: {s.subject || <em className="text-rose-500">no subject</em>}
                          {s.step > 1 && <span className="text-slate-400 font-normal">· +{s.delay_days}d</span>}
                        </div>
                        {(s.attachments || []).length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-1">
                            {s.attachments.map((a, i) => (
                              <span key={i} className="text-[10px] text-slate-500 flex items-center gap-1"><Paperclip className="w-2.5 h-2.5" />{a.filename}</span>
                            ))}
                          </div>
                        )}
                        <div className="mt-1 text-xs text-slate-600 prose prose-sm max-w-none border-t pt-1.5"
                          dangerouslySetInnerHTML={{ __html: s.rendered_html }} />
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Exclusions */}
              <section data-testid="review-exclusions">
                <h4 className="text-xs font-semibold text-slate-600 mb-1">Exclusions — will NOT receive a send ({review.exclusions_count})</h4>
                <div className="border rounded-lg divide-y max-h-40 overflow-y-auto">
                  {(review.exclusions || []).map((x, i) => (
                    <div key={i} className="flex items-center gap-2 px-3 py-1.5 text-xs">
                      <span className="flex-1 truncate text-slate-600">{x.email} {x.company_name ? `· ${x.company_name}` : ""}</span>
                      <Badge variant="outline" className="bg-rose-50 text-rose-600 border-rose-200 text-[10px]">{x.reason}</Badge>
                    </div>
                  ))}
                  {(review.exclusions || []).length === 0 && <div className="px-3 py-2 text-xs text-slate-400">No one excluded.</div>}
                </div>
              </section>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="bg-white border rounded-lg p-2">
    <div className="text-[10px] text-slate-400">{label}</div>
    <div className="text-xs font-semibold text-slate-700">{value}</div>
  </div>
);
